/**
 * Chat messaging hook.
 *
 * Manages the full chat message lifecycle:
 * - Message array (history + live messages)
 * - Streaming state (partial text from SSE deltas)
 * - Send text messages and audio attachments
 * - Abort active requests
 * - Load history from gateway
 * - Model switching
 *
 * Integrates with the chat protocol modules (send, history, streaming)
 * and the FSM for state transitions.
 */

import { useState, useCallback, useRef } from 'react';
import type { SetStateAction } from 'react';
import type { ChatMessage, StreamingState } from '../types/chat';
import { STORAGE_KEYS } from '../utils/constants';
import { readString, writeString } from '../utils/storage';
import { remoteLog } from '../utils/remoteLog';

/** Maximum number of chat messages to keep in state. Older messages are dropped to prevent memory issues. */
const MAX_MESSAGES = 500;

function trimMessages(msgs: ChatMessage[]): ChatMessage[] {
  if (msgs.length <= MAX_MESSAGES) return msgs;
  return msgs.slice(msgs.length - MAX_MESSAGES);
}

// ── Hook Return Type ──

export interface SendMessageUiOptions {
  /** Known request runId/idempotency key from the caller. */
  requestRunId?: string;
  /** Whether to create the optimistic assistant placeholder bubble. */
  createAssistantPlaceholder?: boolean;
}

export interface UseChatReturn {
  /** All chat messages (history + live) */
  messages: ChatMessage[];
  /** Current streaming state (null if not streaming) */
  streaming: StreamingState | null;
  /** Whether a message is currently being streamed */
  isStreaming: boolean;
  /** The accumulated streaming text (empty string if not streaming) */
  streamingText: string;
  /** Currently selected model ID */
  selectedModel: string;
  /** Add a user or system message to the chat */
  addMessage: (message: ChatMessage) => void;
  /** Send a text message (creates a user bubble and, by default, a placeholder assistant bubble) */
  sendMessage: (text: string, gatewayId?: string, gatewayName?: string, options?: SendMessageUiOptions) => string;
  /** Send an audio attachment (base64 encoded) */
  sendAudio: (base64Audio: string, mimeType: string, gatewayId?: string, gatewayName?: string) => string;
  /**
   * Patch the oldest waiting placeholder's temporary runId to the real gateway runId.
   * Called from onStreamStart when the gateway assigns the real runId.
   * Returns true if a placeholder was found and patched.
   */
  patchPlaceholderRunId: (realRunId: string) => boolean;
  /**
   * Patch a specific placeholder (by its tempRunId) to use the real gateway runId.
   * Called eagerly when sendChatMessage() resolves. This is the primary patching
   * mechanism; patchPlaceholderRunId is kept as a fallback.
   */
  patchSpecificPlaceholder: (tempRunId: string, realRunId: string) => boolean;
  /** Update streaming text for an active response */
  updateStreaming: (runId: string, content: string, seq: number) => void;
  /** Finalize a streaming message with the complete text */
  finalizeStreaming: (runId: string, content: string, usage?: { inputTokens?: number; outputTokens?: number }) => void;
  /** Mark a streaming message as aborted */
  abortStreaming: (runId: string) => void;
  /** Mark a streaming message as errored */
  errorStreaming: (runId: string, errorMessage: string) => void;
  /** Abort the current request (if any) */
  abort: () => void;
  /** Load chat history (replaces current messages with history, optionally per-gateway) */
  loadHistory: (historyMessages: ChatMessage[], gatewayId?: string) => void;
  /** Switch the AI model */
  switchModel: (modelId: string) => void;
  /** Clear all messages */
  clearMessages: () => void;
  /** Set messages directly (e.g., from history or updater callbacks) */
  setMessages: (action: SetStateAction<ChatMessage[]>) => void;
  /** The current active run ID (for abort correlation) */
  activeRunId: string | null;
  /** Counter that increments on each history load (used to trigger scroll-to-bottom) */
  historyLoadCount: number;
}

/**
 * Hook for managing chat state and operations.
 *
 * This hook owns the message array and streaming state. It provides
 * methods for the full message lifecycle: send → stream → finalize.
 *
 * Integration with the gateway WebSocket client is done by the parent
 * component, which calls updateStreaming/finalizeStreaming in response
 * to chat.event frames from the StreamingHandler.
 */
export function useChat(): UseChatReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<StreamingState | null>(null);
  const [selectedModel, setSelectedModel] = useState<string>(
    () => readString(STORAGE_KEYS.SELECTED_MODEL, 'default'),
  );
  const activeRunIdRef = useRef<string | null>(null);
  const [historyLoadCount, setHistoryLoadCount] = useState(0);

  /**
   * FIFO queue of temporary placeholder runIds, in creation order.
   * When onStreamStart fires with a real runId, we pop the first entry
   * from this queue to know which placeholder to patch — instead of the
   * old "oldest local-*" heuristic that could mis-match when multiple
   * messages are in flight.
   */
  const placeholderQueueRef = useRef<string[]>([]);

  const removePlaceholderQueueEntry = useCallback((runId: string) => {
    const queueIdx = placeholderQueueRef.current.indexOf(runId);
    if (queueIdx >= 0) {
      placeholderQueueRef.current.splice(queueIdx, 1);
    }
  }, []);

  // ── Message Management ──

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => trimMessages([...prev, message]));
  }, []);

  /**
   * Send a text message. Creates a user message bubble and a placeholder
   * streaming assistant message. Returns a temporary run ID — the real
   * run ID from the gateway will be patched in onStreamStart via
   * patchPlaceholderRunId.
   *
   * @param text - The message text
   * @param gatewayId - Optional gateway ID to tag the message
   * @param gatewayName - Optional gateway name for display
   */
  const sendMessage = useCallback((
    text: string,
    gatewayId?: string,
    gatewayName?: string,
    options?: SendMessageUiOptions,
  ): string => {
    const now = new Date().toISOString();
    const requestRunId = options?.requestRunId ?? `local-${Date.now()}`;
    const createAssistantPlaceholder = options?.createAssistantPlaceholder !== false;

    const userMsg: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: now,
      isHistory: false,
      isStreaming: false,
      ttsStatus: 'none',
      runId: requestRunId,
      gatewayId,
      gatewayName,
    };

    if (!createAssistantPlaceholder) {
      setMessages((prev) => trimMessages([...prev, userMsg]));
      return requestRunId;
    }

    const assistantMsg: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: '',
      timestamp: now,
      isHistory: false,
      isStreaming: true,
      ttsStatus: 'none',
      runId: requestRunId,
      gatewayId,
      gatewayName,
      replyToId: userMsg.id,
      replyToText: text.length > 100 ? text.slice(0, 100) + '…' : text,
    };

    activeRunIdRef.current = requestRunId;
    if (requestRunId.startsWith('local-')) {
      placeholderQueueRef.current.push(requestRunId);
    }
    setMessages((prev) => trimMessages([...prev, userMsg, assistantMsg]));
    setStreaming({
      runId: requestRunId,
      content: '',
      lastSeq: -1,
      hasFirstToken: false,
    });

    // Stale placeholder cleanup — if after 40 s the placeholder still
    // has no content and is streaming, remove it.
    const staleId = assistantMsg.id;
    const staleRunId = requestRunId;
    setTimeout(() => {
      const qi = placeholderQueueRef.current.indexOf(staleRunId);
      if (qi >= 0) placeholderQueueRef.current.splice(qi, 1);

      setMessages((prev) => {
        const msg = prev.find((m) => m.id === staleId && m.role === 'assistant');
        if (msg && msg.isStreaming && !msg.content && !msg.toolCalls?.length && !msg.thinkingText && msg.runId === staleRunId) {
          const assistantIdx = prev.indexOf(msg);
          return prev.filter((_, i) => i !== assistantIdx);
        }
        return prev;
      });
    }, 40_000);

    return requestRunId;
  }, []);

  /**
   * Send an audio attachment. Creates a user message with an audio
   * indicator and a placeholder streaming assistant message.
   *
   * @param base64Audio - Base64-encoded audio data
   * @param mimeType - Audio MIME type
   * @param gatewayId - Optional gateway ID to tag the message
   * @param gatewayName - Optional gateway name for display
   */
  const sendAudio = useCallback(
    (base64Audio: string, mimeType: string, gatewayId?: string, gatewayName?: string): string => {
      const now = new Date().toISOString();
      const tempRunId = `local-${Date.now()}`;

      const userMsg: ChatMessage = {
        id: `user-audio-${Date.now()}`,
        role: 'user',
        content: `🎤 Audio message (${mimeType})`,
        timestamp: now,
        isHistory: false,
        isStreaming: false,
        ttsStatus: 'none',
        gatewayId,
        gatewayName,
      };

      // Suppress unused variable warning — base64Audio is used by
      // the caller when constructing the actual chat.send request
      void base64Audio;

      const assistantMsg: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: '',
        timestamp: now,
        isHistory: false,
        isStreaming: true,
        ttsStatus: 'none',
        runId: tempRunId,
        gatewayId,
        gatewayName,
        replyToId: userMsg.id,
        replyToText: userMsg.content.length > 100 ? userMsg.content.slice(0, 100) + '…' : userMsg.content,
      };

      activeRunIdRef.current = tempRunId;
      placeholderQueueRef.current.push(tempRunId);
      setMessages((prev) => trimMessages([...prev, userMsg, assistantMsg]));
      setStreaming({
        runId: tempRunId,
        content: '',
        lastSeq: -1,
        hasFirstToken: false,
      });

      // Stale placeholder cleanup (same logic as sendMessage — see comment there)
      const staleId = assistantMsg.id;
      const staleTempRunId = tempRunId;
      setTimeout(() => {
        const qi = placeholderQueueRef.current.indexOf(staleTempRunId);
        if (qi >= 0) placeholderQueueRef.current.splice(qi, 1);

        setMessages((prev) => {
          const msg = prev.find((m) => m.id === staleId && m.role === 'assistant');
          if (msg && msg.isStreaming && !msg.content && !msg.toolCalls?.length && !msg.thinkingText && msg.runId?.startsWith('local-')) {
            const assistantIdx = prev.indexOf(msg);
            return prev.filter((_, i) => i !== assistantIdx);
          }
          return prev;
        });
      }, 40_000);

      return tempRunId;
    },
    [],
  );

  // ── Run ID Patching ──

  /**
   * Patch the next waiting placeholder's temporary runId to the real gateway runId.
   *
   * Uses a FIFO queue (`placeholderQueueRef`) instead of scanning for the oldest
   * `local-*` message. The gateway processes messages serially per session, so the
   * first `onStreamStart` always corresponds to the first placeholder we created.
   *
   * If the eager patch (patchSpecificPlaceholder) already ran, the message will
   * already carry `realRunId` — we detect that and skip without popping the queue
   * (the eager path already removed the entry).
   *
   * Returns true if a placeholder was found and patched.
   */
  const patchPlaceholderRunId = useCallback((realRunId: string): boolean => {
    let patched = false;
    let poppedTempRunId: string | undefined;

    setMessages((prev) => {
      // If a message already carries this realRunId, keep the placeholder queue
      // clean as well. This happens for caller-specified runIds that were already
      // final at placeholder creation time.
      if (prev.some((m) => m.runId === realRunId && m.role === 'assistant')) {
        removePlaceholderQueueEntry(realRunId);
        patched = true; // Signal "already done" so callers don't inject duplicates
        return prev;
      }

      // Pop the next tempRunId from the FIFO queue
      poppedTempRunId = placeholderQueueRef.current.shift();
      if (!poppedTempRunId) return prev;

      const idx = prev.findIndex(
        (m) => m.role === 'assistant' && m.runId === poppedTempRunId,
      );
      if (idx < 0) return prev;

      patched = true;
      const updated = [...prev];
      updated[idx] = { ...updated[idx], runId: realRunId };
      return updated;
    });

    if (patched && poppedTempRunId) {
      // Patch streaming state if it still references the old tempRunId
      setStreaming((prev) => {
        if (prev && prev.runId === poppedTempRunId) {
          return { ...prev, runId: realRunId };
        }
        return prev;
      });

      if (activeRunIdRef.current === poppedTempRunId) {
        activeRunIdRef.current = realRunId;
      }
    }

    return patched;
  }, [removePlaceholderQueueEntry]);

  /**
   * Patch a SPECIFIC placeholder (identified by its tempRunId) to use the real
   * gateway runId. Unlike patchPlaceholderRunId which blindly patches the oldest
   * local-* placeholder, this method targets the exact placeholder that was created
   * for a specific sendMessage() call.
   *
   * This is the primary patching mechanism — called eagerly when sendChatMessage()
   * resolves with the real runId. patchPlaceholderRunId is kept as a fallback for
   * server-initiated messages and subscribed sessions.
   *
   * Returns true if the placeholder was found and patched.
   */
  const patchSpecificPlaceholder = useCallback((tempRunId: string, realRunId: string): boolean => {
    let patched = false;

    setMessages((prev) => {
      // Remove from FIFO queue INSIDE the updater so the mutation is atomic
      // with the state update. Previously this ran synchronously outside,
      // which raced with patchPlaceholderRunId: the queue entry would be gone
      // before patchPlaceholderRunId's updater could use it, yet THIS updater
      // hadn't applied the patch yet — causing the onStreamStart inject path
      // to create a duplicate message.
      const queueIdx = placeholderQueueRef.current.indexOf(tempRunId);
      if (queueIdx >= 0) {
        placeholderQueueRef.current.splice(queueIdx, 1);
      }

      // If a message already carries realRunId (patchPlaceholderRunId already
      // ran, or a previous call already patched), treat as "already done".
      if (prev.some((m) => m.role === 'assistant' && m.runId === realRunId)) {
        patched = true;
        return prev;
      }

      const idx = prev.findIndex(
        (m) => m.role === 'assistant' && m.runId === tempRunId,
      );
      if (idx < 0) return prev;

      patched = true;

      // Check if another message already has the realRunId (e.g., a thinking
      // event arrived before this patch and created a separate message).
      // If so, merge the thinking message's properties INTO the placeholder
      // (which has the earlier array position — important for findIndex-based
      // tool call and delta routing) and remove the duplicate.
      const dupeIdx = prev.findIndex(
        (m) => m.role === 'assistant' && m.runId === realRunId,
      );

      if (dupeIdx >= 0 && dupeIdx !== idx) {
        const dupe = prev[dupeIdx];
        const merged = {
          ...prev[idx],
          runId: realRunId,
          // Absorb thinking/tool state from the duplicate
          ...(dupe.isThinking !== undefined ? { isThinking: dupe.isThinking } : {}),
          ...(dupe.thinkingText ? { thinkingText: dupe.thinkingText } : {}),
          ...(dupe.thinkingDuration !== undefined ? { thinkingDuration: dupe.thinkingDuration } : {}),
          ...(dupe.toolCalls?.length ? { toolCalls: dupe.toolCalls } : {}),
          ...(dupe.agentLabel ? { agentLabel: dupe.agentLabel } : {}),
        };
        const updated = [...prev];
        updated[idx] = merged;
        return updated.filter((_, i) => i !== dupeIdx);
      }

      const updated = [...prev];
      updated[idx] = { ...updated[idx], runId: realRunId };
      return updated;
    });

    // Also patch streaming state if it matches this specific tempRunId
    setStreaming((prev) => {
      if (prev && prev.runId === tempRunId) {
        return { ...prev, runId: realRunId };
      }
      return prev;
    });

    if (activeRunIdRef.current === tempRunId) {
      activeRunIdRef.current = realRunId;
    }

    return patched;
  }, []);

  // ── Streaming ──

  /**
   * Append a delta to the streaming message.
   * Called by the StreamingHandler's onDelta callback.
   *
   * Matches by exact runId only — the placeholder's runId must have been
   * patched to the real gateway runId via patchPlaceholderRunId() before
   * deltas arrive. This prevents response text from bleeding into wrong
   * message bubbles when multiple messages are in flight.
   */
  const updateStreaming = useCallback(
    (runId: string, content: string, seq: number) => {
      setStreaming((prev) => {
        if (prev && prev.runId === runId) {
          return {
            ...prev,
            content: prev.content + content,
            lastSeq: seq,
            hasFirstToken: true,
          };
        }
        // New stream started (e.g., server-initiated message)
        return {
          runId,
          content,
          lastSeq: seq,
          hasFirstToken: true,
        };
      });

      // Update the message in the messages array — exact runId match only
      setMessages((prev) => {
        const idx = prev.findIndex(
          (m) => m.runId === runId && m.role === 'assistant',
        );
        if (idx >= 0) {
          const updated = [...prev];
          updated[idx] = {
            ...updated[idx],
            content: updated[idx].content + content,
          };
          return updated;
        }
        return prev;
      });
    },
    [],
  );

  /**
   * Finalize a streaming message with the complete response text.
   * Called by the StreamingHandler's onComplete callback.
   *
   * Matches by exact runId only — no isStreaming fallback.
   */
  const finalizeStreaming = useCallback(
    (runId: string, content: string, usage?: { inputTokens?: number; outputTokens?: number }) => {
      activeRunIdRef.current = null;
      setStreaming(null);
      setMessages((prev) => {
        const msg = prev.find((m) => m.runId === runId && m.role === 'assistant');
        if (msg) {
          remoteLog('toolcalls', 'debug_finalize', { runId, toolCallCount: msg.toolCalls?.length });
          return prev.map((m) =>
            m.runId === runId && m.role === 'assistant'
              ? {
                  ...m,
                  content,
                  isStreaming: false,
                  toolCalls: m.toolCalls,
                  inputTokens: usage?.inputTokens ?? m.inputTokens,
                  outputTokens: usage?.outputTokens ?? m.outputTokens,
                }
              : m,
          );
        }
        // Proactive message (heartbeat, cron, etc.) — no preceding delta.
        // Insert as a new assistant message so it appears in the chat.
        if (content && content.trim()) {
          return trimMessages([
            ...prev,
            {
              id: `assistant-proactive-${Date.now()}`,
              role: 'assistant' as const,
              content,
              timestamp: new Date().toISOString(),
              isHistory: false,
              isStreaming: false,
              ttsStatus: 'none' as const,
              runId,
              inputTokens: usage?.inputTokens,
              outputTokens: usage?.outputTokens,
            },
          ]);
        }
        return prev;
      });
    },
    [],
  );

  /** Mark a streaming message as aborted — exact runId match only */
  const abortStreaming = useCallback((runId: string) => {
    activeRunIdRef.current = null;
    setStreaming(null);
    setMessages((prev) =>
      prev.map((m) =>
        m.runId === runId && m.role === 'assistant'
          ? {
              ...m,
              isStreaming: false,
              content: m.content || '[Aborted]',
              toolCalls: m.toolCalls,
            }
          : m,
      ),
    );
  }, []);

  /** Mark a streaming message as errored — exact runId match only */
  const errorStreaming = useCallback(
    (runId: string, errorMessage: string) => {
      activeRunIdRef.current = null;
      setStreaming(null);
      setMessages((prev) =>
        prev.map((m) =>
          m.runId === runId && m.role === 'assistant'
            ? {
                ...m,
                isStreaming: false,
                content: m.content || `⚠️ ${errorMessage}`,
                toolCalls: m.toolCalls,
              }
            : m,
        ),
      );
    },
    [],
  );

  /** Abort the current active request */
  const abort = useCallback(() => {
    const runId = activeRunIdRef.current;
    if (runId) {
      abortStreaming(runId);
    }
  }, [abortStreaming]);

  // ── History ──

  /**
   * Load history messages (prepend before live messages).
   *
   * @param historyMessages - Messages to load
   * @param gatewayId - Optional: only replace history for this gateway
   */
  const loadHistory = useCallback((historyMessages: ChatMessage[], gatewayId?: string) => {
    setMessages((prev) => {
      if (gatewayId) {
        // Replace only history from this specific gateway
        const otherMessages = prev.filter(
          (m) => !(m.isHistory && m.gatewayId === gatewayId),
        );
        // Insert new history at the beginning, before live messages
        const liveMessages = otherMessages.filter((m) => !m.isHistory);
        const otherHistory = otherMessages.filter((m) => m.isHistory);
        return trimMessages([...otherHistory, ...historyMessages, ...liveMessages]);
      }
      // Fallback: replace all history
      const liveMessages = prev.filter((m) => !m.isHistory);
      return trimMessages([...historyMessages, ...liveMessages]);
    });
    // Signal Chat to force-scroll to bottom after history load
    setHistoryLoadCount((c) => c + 1);
  }, []);

  // ── Model ──

  /** Switch the AI model and persist to localStorage */
  const switchModel = useCallback((modelId: string) => {
    setSelectedModel(modelId);
    writeString(STORAGE_KEYS.SELECTED_MODEL, modelId);
  }, []);

  // ── Clear ──

  const clearMessages = useCallback(() => {
    setMessages([]);
    setStreaming(null);
    activeRunIdRef.current = null;
    placeholderQueueRef.current = [];
  }, []);

  return {
    messages,
    streaming,
    isStreaming: streaming !== null,
    streamingText: streaming?.content ?? '',
    selectedModel,
    addMessage,
    sendMessage,
    sendAudio,
    patchPlaceholderRunId,
    patchSpecificPlaceholder,
    updateStreaming,
    finalizeStreaming,
    abortStreaming,
    errorStreaming,
    abort,
    loadHistory,
    switchModel,
    clearMessages,
    setMessages,
    activeRunId: activeRunIdRef.current,
    historyLoadCount,
  };
}
